/**
 * Network plumbing for the reference tools — browser-side, and only on a button press.
 *
 * Four services, each named on the page that uses it: Crossref (DOI Finder), OpenAlex
 * (Open Access Finder), the ORCID public API and CitationEasy's own `/api/cite`
 * (ORCID Publication List). All four answer browsers directly and need no key.
 *
 * Every one of them rate-limits bursts — Crossref's public pool allows a single list
 * query in flight — so batches run through `runPool`, which caps how many requests are
 * open at once and how soon the next one may start, and a 429 is retried after the
 * wait the server asks for rather than hammered.
 */

export type FetchFailure =
  | "not-found"
  | "rate-limited"
  | "unavailable"
  | "timeout"
  | "network";

export type FetchResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: FetchFailure; status?: number };

export interface FetchJsonOptions {
  signal: AbortSignal;
  init?: RequestInit;
  /** Give up on one attempt after this long. */
  timeoutMs?: number;
  /** Extra attempts after a 429 or a 5xx. */
  retries?: number;
}

const DEFAULT_TIMEOUT_MS = 20_000;
const DEFAULT_RETRY_WAIT_MS = 2000;
const MAX_RETRY_WAIT_MS = 10_000;

/** Resolves after `ms`, or as soon as `signal` aborts. Never rejects. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted || ms <= 0) {
      resolve();
      return;
    }
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    }
    signal?.addEventListener("abort", done, { once: true });
  });
}

/** `Retry-After` in seconds (the only form these APIs send), clamped to something sane. */
function retryWait(response: Response): number {
  const header = Number(response.headers.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) {
    return Math.min(header * 1000, MAX_RETRY_WAIT_MS);
  }
  return DEFAULT_RETRY_WAIT_MS;
}

type Attempt<T> =
  | FetchResult<T>
  | { ok: false; reason: "retry"; waitMs: number };

async function attempt<T>(
  url: string,
  options: FetchJsonOptions,
  canRetry: boolean
): Promise<Attempt<T>> {
  const local = new AbortController();
  let timedOut = false;
  const forward = () => local.abort();
  options.signal.addEventListener("abort", forward, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    local.abort();
  }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      ...options.init,
      signal: local.signal,
    });
    if (response.ok) {
      const body = (await response.json().catch(() => null)) as T | null;
      return body === null
        ? { ok: false, reason: "unavailable", status: response.status }
        : { ok: true, value: body };
    }
    const retryable = response.status === 429 || response.status >= 500;
    if (retryable && canRetry) {
      return { ok: false, reason: "retry", waitMs: retryWait(response) };
    }
    return {
      ok: false,
      reason: failureFor(response.status),
      status: response.status,
    };
  } catch (error) {
    if (options.signal.aborted) {
      throw error;
    }
    return { ok: false, reason: timedOut ? "timeout" : "network" };
  } finally {
    clearTimeout(timer);
    options.signal.removeEventListener("abort", forward);
  }
}

function failureFor(status: number): FetchFailure {
  if (status === 404 || status === 410) {
    return "not-found";
  }
  if (status === 429) {
    return "rate-limited";
  }
  return "unavailable";
}

/**
 * GET (or POST, through `init`) a JSON document. Resolves to a typed result for every
 * outcome except a cancelled run: when `signal` aborts, the abort error is rethrown so
 * a superseded batch stops instead of reporting a failure nobody asked about.
 */
export async function fetchJson<T>(
  url: string,
  options: FetchJsonOptions
): Promise<FetchResult<T>> {
  let retriesLeft = options.retries ?? 1;
  for (;;) {
    const result = await attempt<T>(url, options, retriesLeft > 0);
    if (result.ok || result.reason !== "retry") {
      return result;
    }
    retriesLeft -= 1;
    await sleep(result.waitMs, options.signal);
    if (options.signal.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
  }
}

export interface PoolOptions {
  /** Most requests open at once. */
  concurrency: number;
  /** Least time between the starts of two requests. */
  minIntervalMs?: number;
  signal: AbortSignal;
  onProgress?: (done: number) => void;
}

/**
 * Run `worker` over `items` with at most `concurrency` in flight and at least
 * `minIntervalMs` between starts. Results keep the order of `items`. Returns null when
 * `signal` aborts part-way, so a superseded run never overwrites a newer one.
 */
export async function runPool<T, R>(
  items: readonly T[],
  worker: (item: T, index: number) => Promise<R>,
  options: PoolOptions
): Promise<R[] | null> {
  const { signal, minIntervalMs = 0 } = options;
  const results: R[] = [];
  let next = 0;
  let done = 0;
  let nextStart = 0;

  async function waitTurn(): Promise<void> {
    if (minIntervalMs <= 0) {
      return;
    }
    const now = Date.now();
    const at = Math.max(now, nextStart);
    nextStart = at + minIntervalMs;
    await sleep(at - now, signal);
  }

  async function lane(): Promise<void> {
    while (next < items.length && !signal.aborted) {
      const index = next;
      next += 1;
      await waitTurn();
      if (signal.aborted) {
        return;
      }
      results[index] = await worker(items[index], index);
      done += 1;
      options.onProgress?.(done);
    }
  }

  const lanes = Math.max(1, Math.min(options.concurrency, items.length));
  try {
    await Promise.all(Array.from({ length: lanes }, () => lane()));
  } catch (error) {
    if (signal.aborted) {
      return null;
    }
    throw error;
  }
  return signal.aborted ? null : results;
}

/** Split into fixed-size chunks, preserving order. */
export function chunked<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let index = 0; index < items.length; index += Math.max(1, size)) {
    out.push(items.slice(index, index + Math.max(1, size)));
  }
  return out;
}
